import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { RequestSearchCompletedSubmissions,setModalActive,setModalParams } from "../../../store/actions/dashboardActions";
import Spinner from "../Spinner/Spinner";
import AiChatModal from "../../UI/AiChat/AiChatModal";
import SearchIcon from "../../Icon-SVG/SearchIcon";
import CloseIcon from "../../Icon-SVG/CloseIcon";
import {
  getSeverityLabel,
  getSubmissionTags,
  splitRunTime,
} from "../../../utils/function/submissionTags";

import "./styles.scss";

// Severity drives the colour of the marker on the left of every result.
const SEVERITY_CLASS = {
  malicious: "search-results__item--malicious",
  suspicious: "search-results__item--suspicious",
  "not suspicious": "search-results__item--clean",
};

const SearchInput = ({ uilogo = false }) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const modalActive = useSelector((state) => state.dashboard.modalActive);
  const searchWrapperRef = useRef(null);
  const listRef = useRef(null);
  const searchResultsFromApi = useSelector((state) => state.dashboard.searchResults);
  const loading = useSelector((state) => state.dashboard.searchResultsLoading);
  const history = useHistory();
  const dispatch = useDispatch();

  // The panel is open whenever there is something to say: results, a spinner
  // or an empty state.
  const query = searchValue.trim();
  const panelOpen = query.length > 0;

  const handleSelectFile = (file) => {
    const severity = getSeverityLabel(file.decision);
    const url = `/analysis-reports?data=${file.parentID}&state=${severity}`;
    history.push(url, { clickedFileID: file.fileID });
    setSearchValue("");
    setSearchResults([]);
    setActiveIndex(-1);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (!searchValue) {
        setSearchResults([]);
        return;
      }
      dispatch(RequestSearchCompletedSubmissions(searchValue));
    }, 700);

    return () => clearTimeout(delayDebounceFn);
  }, [searchValue]);

  useEffect(() => {
    setSearchResults(searchResultsFromApi);
    setActiveIndex(-1);
  }, [searchResultsFromApi]);

  useEffect(() => {
    if (modalActive) {
      disableBodyScroll(document.body);
    } else {
      enableBodyScroll(document.body);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [modalActive]);

  // Keep the keyboard-selected row inside the scrolling panel.
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;

    const activeItem = listRef.current.children[activeIndex];
    if (activeItem) activeItem.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleClickOutside = (event) => {
    const searchWrapper = searchWrapperRef.current;
    if (searchWrapper && !searchWrapper.contains(event.target)) {
      setSearchValue("");
      setSearchResults([]);
      setActiveIndex(-1);
    }
  };

  const handleChange = (event) => {
    setSearchValue(event.target.value);
  };

  const clearHandler = () => {
    setSearchValue("");
    setSearchResults([]);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      clearHandler();
      return;
    }

    if (!searchResults.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % searchResults.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? searchResults.length - 1 : prev - 1));
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      handleSelectFile(searchResults[activeIndex]);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderResult = (file, index) => {
    const severity = getSeverityLabel(file.decision);
    const { machine, network, analysisType } = getSubmissionTags(file.fileID);
    const { date, time } = splitRunTime(file.run_time);
    // Everything that tells one run of the same file from another.
    const tags = [analysisType, network].filter(Boolean);

    return (
      <li
        key={file.fileID || index}
        className={`search-results__item ${SEVERITY_CLASS[severity] || ""} ${
          index === activeIndex ? "search-results__item--active" : ""
        }`}
        onMouseEnter={() => setActiveIndex(index)}
        onClick={() => handleSelectFile(file)}>
        <div className="search-results__main">
          <span className="search-results__name" title={file.file_name}>
            {file.file_name}
          </span>
          <span className="search-results__severity">{severity}</span>
        </div>
        <div className="search-results__meta">
          {machine ? <span className="search-results__tag">{machine}</span> : null}
          {tags.map((tag) => (
            <span className="search-results__tag" key={tag}>
              {tag}
            </span>
          ))}
          <span className="search-results__time">
            {date}
            {time ? <span className="search-results__clock">{time}</span> : null}
          </span>
        </div>
      </li>
    );
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <div
          className={`flex-1 search-wrapper ${uilogo ? "uilogosfix" : ""}`}
          ref={searchWrapperRef}>
          <div className="search-input flex-1">
            <span className="search-input__icon">
              <SearchIcon width={14} height={14} color="var(--header-search-placeholder-color)" />
            </span>
            <input
              type="text"
              value={searchValue}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Search by file name or hash"
              aria-label="Search submissions by file name or hash"
            />
            {loading ? (
              <span className="search-input__loading">
                <Spinner width={14} height={14} />
              </span>
            ) : searchValue ? (
              <button
                type="button"
                className="search-input__clear"
                title="Clear search"
                onClick={clearHandler}>
                <CloseIcon width={15} height={15} color="var(--header-search-placeholder-color)" />
              </button>
            ) : null}
          </div>

          {panelOpen && (
            <div className="search-results">
              {searchResults.length > 0 ? (
                <>
                  <div className="search-results__header">
                    <span>
                      {searchResults.length} result{searchResults.length > 1 ? "s" : ""}
                    </span>
                    <span className="search-results__hint">&#8593;&#8595; to browse</span>
                  </div>
                  <ul ref={listRef}>{searchResults.map(renderResult)}</ul>
                </>
              ) : (
                <div className="search-results__empty">
                  {loading ? "Searching submissions..." : `No submissions match "${query}"`}
                </div>
              )}
            </div>
          )}
        </div>
        <div
          onClick={() => {
            const customPromptSuggestions = [
              "Could you list the latest IOCs which is generated from submissions?",
              "Give me the latest APT findings in the submissions?",
              "Could you generate python3 api script which can search domain in the SIEM?",
              "Could you generate powershell script which can search registry value?",
            ];

            // First set the parameters, then activate the modal
            dispatch(setModalParams({
              promptSuggestions: customPromptSuggestions,
              opensession: false
            }));
            dispatch(setModalActive(true)); // Use Redux action to open modal
          }}
          className="flex-2 sidebar__logo__textai">
          <span>CyberFortress AI</span>
        </div>
      </div>
      {modalActive && <AiChatModal active={modalActive} setActive={(value) => dispatch(setModalActive(value))} />}
    </>
  );
};

export default SearchInput;
